import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Eye, EyeOff, Trash2 } from 'lucide-react';
import React from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import FeedbackMessage from './feedback-message';

const FormInputField = <FieldName extends string>({
    id,
    label,
    register,
    error,
    placeholder,
    type,
    info = null,
    endAdornment = null,
    isLoading = false,
    canRemove = false,
    onRemove,
}: {
    id: FieldName;
    label: string;
    register: UseFormRegisterReturn<FieldName>;
    error: string | undefined;
    placeholder: string;
    type: string;
    info?: string | null;
    endAdornment?: React.ReactNode | null;
    isLoading?: boolean;
    canRemove?: boolean;
    onRemove?: () => void;
}) => {
    const [showPassword, setShowPassword] = React.useState(false);

    const handleClickShowPassword = React.useCallback(() => setShowPassword((show) => !show), []);

    return (
        <Box sx={{ width: '100%' }} className={`form-input-${id}`}>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                    component="label"
                    htmlFor={id}
                    sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '21px' }}
                >
                    {label}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <TextField
                        id={id}
                        margin="normal"
                        fullWidth
                        size="small"
                        name={id}
                        autoComplete={id}
                        placeholder={placeholder}
                        sx={{
                            paddingBottom: '0px',
                            backgroundColor: 'white',
                            marginTop: '0px',
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                height: '40px',
                                '& fieldset': {
                                    borderColor: '#E4E6EB',
                                },
                            },
                        }}
                        {...register}
                        type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
                        error={!!error}
                        InputProps={{
                            endAdornment:
                                endAdornment && type === 'submit' ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </IconButton>
                                    </InputAdornment>
                                ) : canRemove ? (
                                    <IconButton
                                        onClick={onRemove}
                                        sx={{
                                            '&:hover': {
                                                color: '#FE5C5E',
                                            },
                                        }}
                                    >
                                        <Trash2 size={18} />
                                    </IconButton>
                                ) : null,
                        }}
                        disabled={isLoading}
                    />
                </Box>
            </Box>
            <FeedbackMessage error={error} info={info} />
        </Box>
    );
};

export default FormInputField;
